import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryCollectionModel, CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@ucs/MobiusOverrides/TextField/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ucsAuthenticatedSampleRequest, { SampleAddress, SampleRequestModel } from "@ucs/Handlers/UcsAuthenticatedSampleRequest";
import { ucsGetCountriesAndStates } from "@ucs/Handlers/UcsCustomApis";
import {
    validateAddress1,
    validateCity,
    validateCountry,
    validatePostalCode,
    validateProjectDescription,
    validateQuantityField,
    validateState,
    validateStateName,
} from "@ucs/Helpers/FormFieldsValidation";
import UcsReCaptcha from "@ucs/Components/UcsReCaptcha";
import UCSColors from "@ucs/Utils/UCSColors";
import React, { ChangeEvent, RefObject } from "react";
import { FC, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        currentShipToAddress: getShipToState(state, state.context.session.shipToId),
    };
};

interface OwnProps {
    productName: string;
    productSku: string;
    productBrand: string;
    isModalOpen: boolean;
    toggleModal: Function;
    extendedStyles?: SampleRequestFormStyles;
    redirectOnSubmit : LinkFieldValue;
}

const mapDispatchToProps = {
    ucsAuthenticatedSampleRequest,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: RefObject<ReCAPTCHA | null> },
        boolean
    >(validateReCaptcha),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SampleRequestFormStyles {
    formGroup?: InjectableCss;
    projectAddressGroup?: InjectableCss;
    qtySection?: InjectableCss;
    qtyMessage?: TypographyProps;
    qtyFieldStyles?: TextFieldPresentationProps;
    sectionalTitle?: TypographyProps;
    textFieldStyles?: TextFieldPresentationProps;
    selectStyles?: SelectPresentationProps;
    messageTextArea?: TextAreaProps;
    buttonSection?: InjectableCss;
}

export const sampleRequestFormStyles: SampleRequestFormStyles = {
    formGroup: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                    css`
                        grid-template-columns: 100%;
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    projectAddressGroup: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 20px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        gap: 15px;
                    `,
                    css`
                        gap: 15px;
                    `,
                ]);
            }}
        `,
    },
    qtySection: {
        css: css`
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 16px;
                    `,
                ]);
            }}
        `,
    },
    qtyMessage: {
        size: 14,
        color: UCSColors.gray04,
        css: css`
            line-height: 18px;
        `,
    },
    qtyFieldStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            inputSelect: css`
                width: 130px;
            `,
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    sectionalTitle: {
        size: 13,
        weight: 800,
        color: UCSColors.gray03,
        transform: "uppercase",
        variant: "p",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            margin-top: -4px;
            margin-bottom: 12px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                    `,
                    css`
                        font-size: 12px;
                    `,
                ]);
            }}
        `,
    },
    textFieldStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    selectStyles: {
        labelProps: {
            size: 13,
            weight: 700,
            css: css`
                line-height: 16px;
                letter-spacing: 0.1em;
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 6px !important;
            `,
        },
    },
    messageTextArea: {
        rows: 11,
        css: css`
            resize: vertical;
        `,
    },
    buttonSection: {
        css: css`
            display: flex;
            gap: 10px;
            margin-top: 32px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        & > *:last-child {
                            flex-grow: 1;
                        }
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
};

const Separator = styled.div`
    height: 1px;
    width: 100%;
    margin-top: 32px;
    background-color: ${UCSColors.gray07};
`;

const SampleRequestForm: FC<Props> = ({
    extendedStyles,
    isModalOpen,
    productName,
    productSku,
    productBrand,
    toggleModal,
    currentShipToAddress,
    ucsAuthenticatedSampleRequest,
    redirectOnSubmit,
    validateReCaptcha,
}) => {
    const recaptchaRef: RefObject<ReCAPTCHA | null> = React.createRef();
    const [styles] = useState(() => mergeToNew(sampleRequestFormStyles, extendedStyles));
    const [formLoaded, setFormLoaded] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Selected countries
    const [countries, setCountries] = useState<CountryModel[]>();
    const [projectSelectedCountry, setProjectSelectedCountry] = useState<CountryModel>();
    const [shipToSelectedCountry, setShipToSelectedCountry] = useState<CountryModel>();

    // qty
    const [quantity, setQuantity] = useState("1");
    const [quantityError, setQuantityError] = useState("");

    // project info
    const [message, setMessage] = useState("");
    const [messageError, setMessageError] = useState("");
    const [projectAddress, setProjectAddress] = useState<SampleAddress>({});
    const [projectCountryError, setProjectCountryError] = useState("");
    const [projectStateError, setProjectStateError] = useState("");
    const [projectCityError, setProjectCityError] = useState("");
    // ship to address
    const [shipToAddress, setShipToAddress] = useState<SampleAddress>({
        addressLine1: currentShipToAddress.value?.address1,
        addressLine2: currentShipToAddress.value?.address2,
        countryId: currentShipToAddress.value?.country?.id,
        stateId: currentShipToAddress.value?.state?.id,
        stateName: currentShipToAddress.value?.state?.name,
        city: currentShipToAddress.value?.city,
        postalCode: currentShipToAddress.value?.postalCode,
    });
    const [shipToAddress1Error, setShipToAddress1Error] = useState("");
    const [shipToCountryError, setShipToCountryError] = useState("");
    const [shipToStateError, setShipToStateError] = useState("");
    const [shipToCityError, setShipToCityError] = useState("");
    const [shipToPostalCodeError, setShipToPostalCodeError] = useState("");

    const toasterContext = useContext(ToasterContext);

    React.useEffect(() => {
        setFormLoaded(true);
        if (countries === undefined) {
            ucsGetCountriesAndStates({}).then((value: CountryCollectionModel) => {
                if (value && value.countries) {
                    setCountries(value.countries);
                    if(shipToAddress.countryId){
                        const country = value.countries!.find(c => c.id === shipToAddress.countryId)
                        setShipToSelectedCountry(country);
                    }
                }
            });
        }
    }, []);

    React.useEffect(() => {
        isModalOpen && setFormLoaded(true);
    }, [isModalOpen]);

    React.useEffect(() => {
        if (formLoaded && typeof quantity !== "undefined") {
            validateQuantity();
        }
    }, [quantity]);

    // Project info
    React.useEffect(() => {
        if (formLoaded && typeof message !== "undefined") {
            if (message!.length === 0 || message!.length <= 10) {
                validateMessage();
            }
        }
    }, [message]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.countryId !== "undefined") {
            validateProjectCountry();
        }
    }, [projectAddress.countryId]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.stateId !== "undefined") {
            validateProjectState();
        }
    }, [projectAddress.stateId]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.stateName !== "undefined") {
            validateProjectStateName();
        }
    }, [projectAddress.stateName]);

    React.useEffect(() => {
        if (formLoaded && typeof projectAddress.city !== "undefined") {
            validateProjectCity();
        }
    }, [projectAddress.city]);

    // ship to
    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.addressLine1 !== "undefined") {
            validateShipToAddress1();
        }
    }, [shipToAddress.addressLine1]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.countryId !== "undefined") {
            validateShipToCountry();
            shipToAddress.postalCode && validateShipToPostalCode();
        }
    }, [shipToAddress.countryId]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.stateId !== "undefined") {
            validateShipToState();
        }
    }, [shipToAddress.stateId]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.stateName !== "undefined") {
            validateShipToStateName();
        }
    }, [shipToAddress.stateName]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.city !== "undefined") {
            validateShipToCity();
        }
    }, [shipToAddress.city]);

    React.useEffect(() => {
        if (formLoaded && typeof shipToAddress.postalCode !== "undefined") {
            validateShipToPostalCode() &&
                setShipToAddress({ ...shipToAddress, postalCode: shipToAddress.postalCode.toUpperCase() });
        }
    }, [shipToAddress.postalCode]);

    // VALIDATION
    const validateQuantity = () => {
        const errorMessage = validateQuantityField(quantity);
        setQuantityError(errorMessage);
        return !errorMessage;
    };

    // validation - project
    const validateMessage = () => {
        const errorMessage = validateProjectDescription(message);
        setMessageError(errorMessage);
        return !errorMessage;
    };

    const validateProjectCountry = () => {
        const errorMessage = validateCountry(projectAddress.countryId);
        setProjectCountryError(errorMessage);
        return !errorMessage;
    };

    const validateProjectState = () => {
        const errorMessage = validateState(projectAddress.stateId);
        setProjectStateError(errorMessage);
        return !errorMessage;
    };

    const validateProjectStateName = () => {
        const errorMessage = validateStateName(projectAddress.stateName);
        setProjectStateError(errorMessage);
        return !errorMessage;
    };

    const validateProjectCity = () => {
        const errorMessage = validateCity(projectAddress.city);
        setProjectCityError(errorMessage);
        return !errorMessage;
    };

    // validation - ship to
    const validateShipToAddress1 = () => {
        const errorMessage = validateAddress1(shipToAddress.addressLine1);
        setShipToAddress1Error(errorMessage);
        return !errorMessage;
    };

    const validateShipToCountry = () => {
        const errorMessage = validateCountry(shipToAddress.countryId);
        setShipToCountryError(errorMessage);
        return !errorMessage;
    };

    const validateShipToState = () => {
        const errorMessage = validateState(shipToAddress.stateId);
        setShipToStateError(errorMessage);
        return !errorMessage;
    };

    const validateShipToStateName = () => {
        const errorMessage = validateStateName(shipToAddress.stateName);
        setShipToStateError(errorMessage);
        return !errorMessage;
    };

    const validateShipToCity = () => {
        const errorMessage = validateCity(shipToAddress.city);
        setShipToCityError(errorMessage);
        return !errorMessage;
    };

    const validateShipToPostalCode = () => {
        const errorMessage = shipToSelectedCountry
            ? validatePostalCode(shipToAddress.postalCode, shipToSelectedCountry.abbreviation)
            : validatePostalCode(shipToAddress.postalCode);
        setShipToPostalCodeError(errorMessage);
        return !errorMessage;
    };

    // EVENT handlers
    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setQuantity(event.target.value);
    };

    const projectCountyChangeHandler: SelectProps["onChange"] = event => {
        const country = countries!.find(c => c.id === event.target.value);
        setProjectSelectedCountry(country);
        setProjectAddress({ ...projectAddress, countryId: country?.id, stateId: "" });
    };

    const projectStateNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setProjectAddress({ ...projectAddress, stateName: event.target.value });
    };

    const projectStateChangeHandler: SelectProps["onChange"] = event => {
        const state = projectSelectedCountry?.states?.find(s => s.id === event.target.value);
        setProjectAddress({ ...projectAddress, stateId: state?.id });
    };

    const projectCityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setProjectAddress({ ...projectAddress, city: event.target.value });
    };

    const shipToAddress1ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, addressLine1: event.target.value });
    };

    const shipToAddress2ChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, addressLine2: event.target.value });
    };

    const shipToCountyChangeHandler: SelectProps["onChange"] = event => {
        const country = countries!.find(c => c.id === event.target.value);
        setShipToSelectedCountry(country);
        setShipToAddress({ ...shipToAddress, countryId: country?.id, stateId: "" });
    };

    const shipToStateNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, stateName: event.target.value });
    };

    const shipToStateChangeHandler: SelectProps["onChange"] = event => {
        const state = shipToSelectedCountry?.states?.find(s => s.id === event.target.value);
        setShipToAddress({ ...shipToAddress, stateId: state?.id });
    };

    const shipToCityChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, city: event.target.value });
    };

    const shipToPostalCodeChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setShipToAddress({ ...shipToAddress, postalCode: event.target.value });
    };

    const qtyKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const populateModel = () => {
        const model: SampleRequestModel = {
            firstName: "",
            lastName: "",
            companyName: "",
            phoneNumber: "",
            emailAddress: "",
            message,
            productName,
            productSku,
            productBrand,
            projectAddress,
            quantity: +quantity,
            billingAddress: {},
            shippingAddress: shipToAddress,
            location: getCookie("SelectedLocation") || "",
            emailTo: getCookie("SalesEmailCookie") || "info@ucfp.com",
            topic: (siteMessage("Request_Sample_Topic") as string) || "Guest sample request",
        };
        return model;
    };

    const clearForm = () => {
        setFormLoaded(false);
        setMessage("");
        setProjectAddress({});
        setProjectCountryError("");
        setProjectStateError("");
        setProjectCityError("");
        setShipToAddress({});
        setShipToAddress1Error("");
        setShipToCountryError("");
        setShipToStateError("");
        setShipToCityError("");
        setShipToPostalCodeError("");
    };

    const validateForm = async () => {
        const validationResult: boolean[] = [         
            validateMessage(),
            validateProjectCountry(),
            projectSelectedCountry && projectSelectedCountry.states && projectSelectedCountry.states.length > 0
                ? validateProjectState()
                : validateProjectStateName(),
            validateProjectCity(),
            validateShipToAddress1(),
            validateShipToCountry(),
            shipToSelectedCountry && shipToSelectedCountry.states && shipToSelectedCountry.states.length > 0
                ? validateShipToState()
                : validateShipToStateName(),
            validateShipToCity(),
            validateShipToPostalCode(),
        ];

        if (!validationResult.includes(false)) {
            const isReCaptchaValid = await validateReCaptcha({ location: "SampleRequest", recaptchaRef });
            if (!isReCaptchaValid) return;
            setIsSubmitting(true);
            const sampleRequestModel = populateModel();
            ucsAuthenticatedSampleRequest({
                sampleRequestModel,
                onSuccess: () => {
                    setIsSubmitting(false);                    
                    if(redirectOnSubmit.value){                        
                        window.location.href = redirectOnSubmit.value;
                    } 
                    else{
                        toasterContext.addToast({
                            body: siteMessage("Request_Sample_Success"),
                            messageType: "success",
                        });
                        toggleModal();
                    } 
                    clearForm();
                },
                onComplete() {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.();
                },
            });
        } else {
            toasterContext.addToast({
                body: siteMessage("Request_Sample_ValidationError"),
                messageType: "danger",
            });
        }
    };

    return (
        <form id="sampleRequestForm" noValidate>
            <StyledWrapper {...styles.qtySection}>
                <TextField
                    {...styles.qtyFieldStyles}
                    label={translate("Quantity")}
                    value={quantity}
                    error={quantityError}
                    type="number"
                    onKeyPress={qtyKeyPressHandler}
                    onChange={qtyChangeHandler}
                    {...styles.qtyFieldStyles}
                />
                <Typography {...styles.qtyMessage}>{siteMessage("Authenticated_User_Request_Sample_Message")}</Typography>
            </StyledWrapper>

            <Separator />
            <Typography {...styles.sectionalTitle}>{translate("Project Info")}</Typography>

            <StyledWrapper {...styles.formGroup}>
                <TextArea
                    {...styles.messageTextArea}
                    label={translate("Project short description")}
                    required={true}
                    minLength={10}
                    error={messageError}
                    value={message}
                    onChange={messageChangeHandler}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <StyledWrapper {...styles.projectAddressGroup}>
                    <Select
                        {...styles.selectStyles}
                        label={translate("Country")}
                        value={projectAddress.countryId}
                        disabled={!countries || countries.length === 0}
                        required={true}
                        onChange={projectCountyChangeHandler}
                        error={projectCountryError}
                    >
                        <option value="">{translate("Select Country")}</option>
                        {countries &&
                            countries.map(co => (
                                <option key={co.id} value={co.id}>
                                    {co.name}
                                </option>
                            ))}
                    </Select>
                    {!projectSelectedCountry || projectSelectedCountry.states!.length > 0 ? (
                        <Select
                            {...styles.selectStyles}
                            label={translate("State / Province")}
                            value={projectAddress.stateId}
                            required={true}
                            onChange={projectStateChangeHandler}
                            error={projectStateError}
                            disabled={!projectSelectedCountry}
                        >
                            <option value="">{translate("Select State / Province")}</option>
                            {projectSelectedCountry?.states?.map(st => (
                                <option key={st.id} value={st.id}>
                                    {st.name}
                                </option>
                            ))}
                        </Select>
                    ) : (
                        <TextField
                            {...styles.textFieldStyles}
                            label={translate("State / Province")}
                            value={projectAddress.stateName || ""}
                            required={true}
                            minLength={2}
                            error={projectStateError}
                            onChange={projectStateNameChangeHandler}
                            autoComplete="off"
                            autoCorrect="off"
                            spellCheck={false}
                        />
                    )}

                    <TextField
                        {...styles.textFieldStyles}
                        label={translate("City")}
                        value={projectAddress.city}
                        required={true}
                        maxLength={60}
                        onChange={projectCityChangeHandler}
                        error={projectCityError}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                </StyledWrapper>
            </StyledWrapper>

            <Separator />
            <Typography {...styles.sectionalTitle}>{translate("Ship to address")}</Typography>

            <TextField
                {...styles.textFieldStyles}
                name="address1"
                label={translate("Address - Line 1")}
                value={shipToAddress.addressLine1}
                required={true}
                maxLength={60}
                onChange={shipToAddress1ChangeHandler}
                error={shipToAddress1Error}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />
            <TextField
                {...styles.textFieldStyles}
                label={translate("Address - Line 2")}
                value={shipToAddress.addressLine2}
                required={false}
                maxLength={60}
                onChange={shipToAddress2ChangeHandler}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
            />

            <StyledWrapper {...styles.formGroup}>
                <Select
                    {...styles.selectStyles}
                    label={translate("Country")}
                    value={shipToAddress.countryId}
                    disabled={!countries || countries.length === 0}
                    required={true}
                    onChange={shipToCountyChangeHandler}
                    error={shipToCountryError}
                >
                    <option value="">{translate("Select Country")}</option>
                    {countries &&
                        countries.map(co => (
                            <option key={co.id} value={co.id}>
                                {co.name}
                            </option>
                        ))}
                </Select>

                {!shipToSelectedCountry || shipToSelectedCountry.states!.length > 0 ? (
                    <Select
                        {...styles.selectStyles}
                        label={translate("State / Province")}
                        value={shipToAddress.stateId}
                        required={true}
                        onChange={shipToStateChangeHandler}
                        error={shipToStateError}
                        disabled={!shipToSelectedCountry}
                    >
                        <option value="">{translate("Select State / Province")}</option>
                        {shipToSelectedCountry?.states?.map(st => (
                            <option key={st.id} value={st.id}>
                                {st.name}
                            </option>
                        ))}
                    </Select>
                ) : (
                    <TextField
                        {...styles.textFieldStyles}
                        label={translate("State / Province")}
                        value={shipToAddress.stateName || ""}
                        required={true}
                        minLength={2}
                        error={shipToStateError}
                        onChange={shipToStateNameChangeHandler}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                    />
                )}
            </StyledWrapper>

            <StyledWrapper {...styles.formGroup}>
                <TextField
                    {...styles.textFieldStyles}
                    label={translate("City")}
                    value={shipToAddress.city}
                    required={true}
                    maxLength={60}
                    onChange={shipToCityChangeHandler}
                    error={shipToCityError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <TextField
                    {...styles.textFieldStyles}
                    label={translate("Zip / Postal Code")}
                    value={shipToAddress.postalCode}
                    required={true}
                    onChange={shipToPostalCodeChangeHandler}
                    error={shipToPostalCodeError}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                />
            </StyledWrapper>

            <UcsReCaptcha recaptchaRef={recaptchaRef} location="SampleRequest" />
            <StyledWrapper {...styles.buttonSection}>
                <Button
                    onClick={e => {
                        e.preventDefault();
                        validateForm();
                    }}
                    disabled={isSubmitting}
                >
                    {translate("Submit Sample Order")}
                </Button>
                <Button
                    variant="secondary"
                    onClick={e => {
                        e.preventDefault();
                        toggleModal();
                        clearForm();
                    }}
                >
                    {translate("Cancel")}
                </Button>
            </StyledWrapper>
        </form>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SampleRequestForm);
